import React, {useEffect} from "react";
import {State, ViewStates} from "@common/components/view-states";
import {ViewState} from "@common/models/enums/viev-state.enum";

import {useArticleForm} from "../../hooks/use-article-form.hook";
import {SpecialistListResponse} from "../../../core/models/interfaces/specialist-list-response.interface";
import {SpecialistSearchResultDto} from "../../../core/models/interfaces/specialists-search-result-dto.interface";
import {SpecialistList, SpecialistSearchbar} from "../../../specialists";
import {useSpecialistApi} from "../../../core/hooks/use-specialist-api";
import SelectedArticleSpecialistTag from "../ui/selected-article-specialist-tag";
import AppButton from "@common/components/app-button";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {ReactComponent as PlusIcon} from "@assets/icons/plus.svg";
import SpecialistFormModal, {SpecialistFormModalData} from "../../../specialists/components/specialist-form-modal";
import {useLoading} from "../../../core/hooks/use-loading.hook";
import {LoadingKeys} from "../../../core/models/enums/loading-keys.enum";

const ArticleFormAuthor = () => {
    const {form} = useArticleForm();
    const {isLoading} = useLoading();
    const [results, setResults] = React.useState<SpecialistSearchResultDto[]>([]);
    const [query, setQuery] = React.useState("");
    const [totalCount, setTotalCount] = React.useState(0);
    const [offset, setOffset] = React.useState(0);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const LIMIT = 20;
    const sentinelRef = React.useRef<HTMLDivElement | null>(null);
    const observerRef = React.useRef<IntersectionObserver | null>(null);
    const {getSearchSpecialists} = useSpecialistApi();
    const {showModal: showSpecialistFormModal} = useModalWithData<SpecialistFormModalData>(ModalIds.SPECIALIST_FORM);

    const handleEmitResults = (res: SpecialistListResponse, q: string) => {
        setResults(res?.specialists ?? []);
        setTotalCount(res?.totalCount ?? 0);
        setOffset((res?.specialists ?? []).length);
        setQuery(q);
    };

    const handleSelectAuthor = (specialist: SpecialistSearchResultDto) => {
        form.setValue("authorId", specialist.id, {shouldDirty: true, shouldValidate: true});
        form.setValue("authorName", specialist.fullName, {shouldDirty: true, shouldValidate: true});
    };

    const selectedAuthorId = form.watch("authorId");

    const hasMore = results.length < totalCount;
    const isInitialLoading = isLoading(LoadingKeys.USE_EXERCISE_API) && results.length === 0;

    const loadMore = async () => {
        if (!hasMore || isLoadingMore) return;
        try {
            setIsLoadingMore(true);
            const res = await getSearchSpecialists(query, LIMIT, offset);
            setResults(prev => [...prev, ...res.specialists]);
            setOffset(prev => prev + LIMIT);
        } catch (e) {
            console.error('Error loading more authors', e);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const handleCreateNewAuthor = (): void => {
        showSpecialistFormModal({
            specialistId: undefined,
            onSuccess: loadInitialSpecialists
        });
    };

    const loadInitialSpecialists = async () => {
        try {
            const res = await getSearchSpecialists("", LIMIT, 0);
            setResults(res.specialists);
            setTotalCount(res.totalCount);
            setOffset(LIMIT);
        } catch (error) {
            console.error('Error loading specialists:', error);
        }
    };

    useEffect(() => {
        void loadInitialSpecialists();
    }, []);

    useEffect(() => {
        if (!sentinelRef.current) return;
        observerRef.current?.disconnect();
        observerRef.current = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
                void loadMore();
            }
        }, {root: null, rootMargin: '200px', threshold: 0});
        observerRef.current.observe(sentinelRef.current);
        return () => observerRef.current?.disconnect();
    }, [query, offset, totalCount, results.length]);

    return (
        <>
            <div className="flex flex-col gap-6 ">
                <h2 className="font-semibold text-2xl">Autor artykułu</h2>
                <span className="font-semibold text-lg">Wybierz autora artykułu (autorzy to specjaliści).</span>
            </div>
            <div className="bg-white z-10 sticky top-0 flex flex-col gap-4 py-6 border-b border-outline-mild">
                <div className="flex gap-4 items-center flex-wrap">
                    <div className="flex gap-4 items-center w-full">
                        <SpecialistSearchbar
                            emitResults={handleEmitResults}
                            onQueryChange={setQuery}
                        />
                        <AppButton
                            onClick={handleCreateNewAuthor}
                            className="bg-primary hover:bg-primary/80 w-fit rounded-[4rem] h-[40px] min-w-fit">
                            <div className="flex gap-2 text-red-400 justify-center items-center whitespace-nowrap">
                                <span className="font-semibold text-base text-on-primary ">Nowy autor</span>
                                <PlusIcon className="text-on-primary"/>
                            </div>
                        </AppButton>
                    </div>
                    {selectedAuthorId && (
                        <SelectedArticleSpecialistTag
                            onRemove={() => {
                                form.setValue("authorId", undefined as any, {shouldDirty: true});
                                form.setValue("authorName", undefined as any, {shouldDirty: true});
                            }}
                            id={selectedAuthorId}
                            fullName={form.getValues().authorName!}/>
                    )}
                </div>
            </div>
            <div className="pt-6">
                <ViewStates
                    loading={isInitialLoading}
                    error={null}
                    data={results}
                >
                    <State state={ViewState.LOADING}/>
                    <State state={ViewState.EMPTY}>
                        {query ? (
                            <span className="text-on-surface/60">Brak wyników dla „{query}”.</span>
                        ) : (
                            <span className="text-on-surface/60">Wpisz, aby wyszukać autora.</span>
                        )}
                    </State>
                    <State state={ViewState.FETCHED}>
                        <SpecialistList
                            specialists={results}
                            showActionsCard={false}
                            onClick={handleSelectAuthor as any}
                            isSpecialistSelected={(id) => id === selectedAuthorId}
                            isLoadingMore={isLoadingMore}
                        />
                        <div ref={sentinelRef}/>
                    </State>
                    <State state={ViewState.ERROR}/>
                </ViewStates>
            </div>

            <SpecialistFormModal/>
        </>
    )
}

export default ArticleFormAuthor
